import { Box, Button, Card, CardBody, CardFooter, CardHeader, Center, Flex, HStack, Heading, Link, Spacer, Text, VStack, Wrap, WrapItem } from "@chakra-ui/react";
import { NavBar } from "../components/NavBar";
import { ArbNovaIcon, BaseIcon, CkbIcon, EthIcon, GodwokenIcon, JoyIDIcon, LineaIcon, MetaMaskIcon, OpBnbIcon, PgnIcon, PolygonIcon, ScrollIcon, StarkNetIcon, TaikoIcon, ZkSyncIcon, ZoraIcon } from "../icons/Icons";
import { Footer } from "../components/Footer";
import { ChainsNavigator } from "../components/ChainsNavigator";
import { useEffect, useRef, useState } from "react";
import { Ethereum } from "../visualizer/Ethereum";
import { Godwoken } from "../visualizer/Godwoken";
import { Linea } from "../visualizer/Linea";
import { ZkSync } from "../visualizer/ZkSync";
import { Zora } from "../visualizer/Zora";
import { Coinbase } from "../visualizer/Coinbase";
import { PolygonZkEvm } from "../visualizer/PolygonZkEvm";
import { PGN } from "../visualizer/PGN";
import { OpBnb } from "../visualizer/OpBnb";
import { ArbNova } from "../visualizer/ArbNova";
import { Nervos } from "../visualizer/Nervos";
import { StarkNet } from "../visualizer/StarkNet";
import { ChainCardHeader } from "../components/ChainNetworkBadge";
import { ChainsInfo } from "../data/ChainsInfo";
import { Scroll } from "../visualizer/Scroll";
import { Taiko } from "../visualizer/Taiko";

export const HomePage = () => {
    const svgDivEthereum = useRef(null);
    const svgDivArbNova = useRef(null);
    const svgDivBase = useRef(null);
    const svgDivGodwoken = useRef(null);
    const svgDivLinea = useRef(null);
    const svgDivOpBnb = useRef(null);
    const svgDivPgn = useRef(null);
    const svgDivPolygonZkEvm = useRef(null);
    const svgDivScroll = useRef(null);
    const svgDivTaiko = useRef(null);
    const svgDivZkSync = useRef(null);
    const svgDivZora = useRef(null);
    const svgDivNervos = useRef(null);
    const svgDivStarkNet = useRef(null);
    const hex40 = "4206942069420694206942069420694206942069";
    const hex64 = "4206942069420694206942069420694206942069420694206942069420691234";
    const hex128 = "42069420694206942069420694206942069420694206942069420694206942069420694206942069420694206942069420694206942069420694206942069420";

    let rendered = false;
    const renderSvgs = () => {
        if(rendered) return;
        if(!svgDivEthereum.current || !svgDivZkSync.current) return;

        rendered = true;

        const vizEthereum = new Ethereum(true);
        const svgEthereum = vizEthereum.generate(hex40, true);
        (svgDivEthereum.current as any).appendChild(svgEthereum);

        const vizArbNova = new ArbNova(true);
        const svgArbNova = vizArbNova.generate(hex40, true);
        (svgDivArbNova.current as any).appendChild(svgArbNova);

        const vizBase = new Coinbase(true);
        const svgBase = vizBase.generate(hex40, true);
        (svgDivBase.current as any).appendChild(svgBase);

        const vizGodwoken = new Godwoken(true);
        const svgGodwoken = vizGodwoken.generate(hex40, true);
        (svgDivGodwoken.current as any).appendChild(svgGodwoken);

        const vizLinea = new Linea(true);
        const svgLinea = vizLinea.generate(hex40, true);
        (svgDivLinea.current as any).appendChild(svgLinea);

        const vizOpBnb = new OpBnb(true);
        const svgOpBnb = vizOpBnb.generate(hex40, true);
        (svgDivOpBnb.current as any).appendChild(svgOpBnb);

        const vizPgn = new PGN(true);
        const svgPgn = vizPgn.generate(hex40, true);
        (svgDivPgn.current as any).appendChild(svgPgn);

        const vizPolygonZkEvm = new PolygonZkEvm(true);
        const svgPolygonZkEvm = vizPolygonZkEvm.generate(hex40, true);
        (svgDivPolygonZkEvm.current as any).appendChild(svgPolygonZkEvm);

        const vizScroll = new Scroll(true);
        const svgScroll = vizScroll.generate(hex40, true);
        (svgDivScroll.current as any).appendChild(svgScroll);

        const vizTaiko = new Taiko(true);
        const svgTaiko = vizTaiko.generate(hex40, true);
        (svgDivTaiko.current as any).appendChild(svgTaiko);

        const vizZkSync = new ZkSync(true);
        const svgZkSync = vizZkSync.generate(hex40, true);
        (svgDivZkSync.current as any).appendChild(svgZkSync);

        const vizZora = new Zora(true);
        const svgZora = vizZora.generate(hex40, true);
        (svgDivZora.current as any).appendChild(svgZora);

        const vizNervos = new Nervos(true);
        const svgNervos = vizNervos.generate(hex128, true);
        (svgDivNervos.current as any).appendChild(svgNervos);
        
        const vizStarkNet = new StarkNet(true);
        const svgStarkNet = vizStarkNet.generate(hex64, true);
        (svgDivStarkNet.current as any).appendChild(svgStarkNet);
    }

    useEffect(() => {
        renderSvgs();
    });

    return (
        <VStack spacing={4} maxW="960px" mx="auto">
            <NavBar />
            <Heading as='h1' size='4xl' noOfLines={1} m={10}>
                Hello, multi chains!
            </Heading>
            <Wrap spacing={4}>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Ethereum" icon={<EthIcon/>} mainnetInfo={ChainsInfo.ethereum.mainnet} />
                        <CardBody>
                            <div id="svgDivEthereum" ref={svgDivEthereum} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Arbitrum Nova" icon={<ArbNovaIcon/>} mainnetInfo={ChainsInfo.arbNova.mainnet} />
                        <CardBody>
                            <div id="svgDivArbNova" ref={svgDivArbNova} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Base" icon={<BaseIcon/>} mainnetInfo={ChainsInfo.base.mainnet} />
                        <CardBody>
                            <div id="svgDivBase" ref={svgDivBase} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Godwoken" icon={<GodwokenIcon/>} mainnetInfo={ChainsInfo.godwoken.mainnet} />
                        <CardBody>
                            <div id="svgDivGodwoken" ref={svgDivGodwoken} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Linea" icon={<LineaIcon/>} mainnetInfo={ChainsInfo.linea.mainnet} />
                        <CardBody>
                            <div id="svgDivLinea" ref={svgDivLinea} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="opBNB" icon={<OpBnbIcon/>} mainnetInfo={ChainsInfo.opBnb.mainnet} />
                        <CardBody>
                            <div id="svgDivOpBnb" ref={svgDivOpBnb} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="PGN" icon={<PgnIcon/>} mainnetInfo={ChainsInfo.pgn.mainnet} />
                        <CardBody>
                            <div id="svgDivPgn" ref={svgDivPgn} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Polygon zkEVM" icon={<PolygonIcon/>} mainnetInfo={ChainsInfo.polygonZkEvm.mainnet} />
                        <CardBody>
                            <div id="svgDivPolygonZkEvm" ref={svgDivPolygonZkEvm} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Scroll" icon={<ScrollIcon/>} mainnetInfo={ChainsInfo.scroll.mainnet} />
                        <CardBody>
                            <div id="svgDivScroll" ref={svgDivScroll} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Taiko" icon={<TaikoIcon/>} mainnetInfo={ChainsInfo.taiko.mainnet} />
                        <CardBody>
                            <div id="svgDivTaiko" ref={svgDivTaiko} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="zkSync" icon={<ZkSyncIcon/>} mainnetInfo={ChainsInfo.zksync.mainnet} />
                        <CardBody>
                            <div id="svgDivZkSync" ref={svgDivZkSync} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <ChainCardHeader title="Zora" icon={<ZoraIcon/>} mainnetInfo={ChainsInfo.zora.mainnet} />
                        <CardBody>
                            <div id="svgDivZora" ref={svgDivZora} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <CardHeader>
                            <Heading size='md'>
                                <HStack><CkbIcon/> <Text>Nervos</Text></HStack>
                            </Heading>
                        </CardHeader>
                        <CardBody>
                            <div id="svgDivNervos" ref={svgDivNervos} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
                <WrapItem>
                    <Card w="300px" h="400px">
                        <CardHeader>
                            <Heading size='md'>
                                <HStack><StarkNetIcon/> <Text>StarkNet</Text></HStack>
                            </Heading>
                        </CardHeader>
                        <CardBody>
                            <div id="svgDivStarkNet" ref={svgDivStarkNet} />
                        </CardBody>
                        <CardFooter>
                        </CardFooter>
                    </Card>
                </WrapItem>
            </Wrap>
            <Footer />
        </VStack>
    );
}